import { AboveTheFoldOnlyServerRender } from 'above-the-fold-only-server-render';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import Checkbox from 'app/components/deprecated/shared/Checkbox';
import {
  didNotFindOptionsShape,
  libraryShape,
  paginationShape,
  routerShape,
  userIdentityShape
} from '@bibliocommons/bc-prop-types';
import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';
import Context from '@bibliocommons/context';
import { bindAll } from '@bibliocommons/utils-react';
import { withRouter } from 'app/helpers/routing';

import PrintButton from 'app/components/shared/PrintButton';
import FacetDrawerToggle from 'app/components/deprecated/shared/Facets/FacetDrawerToggle';
import SortDropDown from 'app/components/shared/SortDropDown';
import { FILTER_PREFIX, FILTER_SEPARATOR } from 'app/constants/BibConstants';
import { VIEW } from 'app/constants/SearchConstants';
import getToggledFilter from 'app/helpers/facets/getToggledFilter';
import { hasPromosEnabled } from 'app/helpers/library/features';
import updateSearchQuery from 'app/helpers/search/updateSearchQuery';
import cardShape from 'app/shapes/cardShape';
import {
  activeFiltersShape,
  branchShape,
  catalogSearchOptionsShape,
  searchResultShape,
  searchTypeShape,
  sortBysShape
} from 'app/shapes/search';
import Pagination from 'app/components/shared/Pagination';
import AppliedFilters from '../AppliedFilters';
import DidNotFindExpandablePanel from '../DidNotFindExpandablePanel';
import { PersonalizedPromotionsContainer } from '../PersonalizedPromotions';
import RecommendedFeaturedContentContainer from '../RecommendedFeaturedContent/RecommendedFeaturedContentContainer';
import SearchResultItem from '../SearchResultItem';
import ViewSelector from '../ViewSelector';

import './SearchResults.scss';

const EVENT_CARD_TYPE = 'event';

class SearchResults extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  componentDidMount() {
    this.fetchFeaturedCards();
    this.fetchGroupSearchSummaries();
  }

  componentDidUpdate(prevProps) {
    if (this.props.results !== prevProps.results || this.props.currentUser !== prevProps.currentUser) {
      this.fetchFeaturedCards();
      this.fetchGroupSearchSummaries();
    }
    if (this.props.featuredCards.size && this.props.currentUser) {
      this.fetchInterestedEvents();
    }
  }

  getPrintUrl() {
    const { search } = this.props.router.location;
    return `/v2/print/search${search}`;
  }

  fetchFeaturedCards() {
    const { cardsActions, library, pagination, router, results } = this.props;

    if (!hasPromosEnabled(library)) {
      return;
    }
    if (!router.query) {
      return;
    }
    if (pagination.get('page') !== 1) {
      return;
    }

    const metadataIds = results.map(result => result.representative?.get('id')).filter(id => id);
    if (metadataIds.size === 0) {
      return;
    }

    cardsActions.searchCards({ ...router.query, metadataIds, limit: 3 });
  }

  fetchGroupSearchSummaries() {
    if (this.shouldShowGroupingToggle()) {
      this.props.searchActions.getGroupSearchSummaries(
        this.props.results
          .map(result => result.manifestations)
          .flatten(true)
          .map(result => result.get('id'))
          .toArray()
      );
    }
  }

  fetchInterestedEvents() {
    if (this.context.features.get(TOGGLZ_FEATURES.ENABLE_EVENTS_INTERESTED)) {
      const { featuredCards, eventsActions } = this.props;
      const ids = [];

      featuredCards.map(card =>
        card?.get('cardType') === EVENT_CARD_TYPE ? ids.push(card.getIn(['event', 'eventId'])) : null
      );
      const eventIds = ids.join(',');
      if (ids.length) {
        eventsActions.getMyInterestedEvents({ eventIds });
      }
    }
  }

  unapplyAllFilters() {
    const { router, searchType } = this.props;

    const query = updateSearchQuery({
      currentQuery: router.query,
      page: undefined,
      searchType
    });

    router.push({ query });
  }

  unapplyFilter(fieldId, currentFilter) {
    const { activeFilters, router, searchType } = this.props;

    const query = updateSearchQuery({
      activeFilters,
      currentQuery: router.query,
      searchType,
      page: undefined,
      ...getToggledFilter({
        activeFilters,
        currentFilter,
        fieldId,
        separator: FILTER_SEPARATOR,
        override: false,
        prefix: FILTER_PREFIX
      })
    });

    router.push({ query });
  }

  toggleIsFiltersPinned() {
    const { activeFilters, locked, router, searchType } = this.props;

    const query = updateSearchQuery({
      activeFilters,
      currentQuery: router.query,
      locked: locked ? undefined : 'true',
      searchType
    });

    router.push({ query });
  }

  shouldShowGroupingToggle() {
    return (
      this.props.currentUser &&
      this.props.currentUser.get('showGroupingDebug') &&
      this.props.currentView === VIEW.GROUPED
    );
  }

  renderFeatureInCatalog() {
    const { library } = this.props;

    if (hasPromosEnabled(library)) {
      return null;
    }

    return this.props.library.get('featuredContentEnabled') && <RecommendedFeaturedContentContainer />;
  }

  renderPersonalizedPromotions(itemIndex) {
    const { catalogSearchOptions, pagination, results, featuredCards } = this.props;
    const currentPage = pagination.get('page');
    const currentItemPosition = itemIndex + 1;

    // Piggyback on the search grouping debug logic to also show the
    // content cards debug button. Basically when the search grouping
    // debug checkbox is checked, also show the debug button for cards.
    const debugEnabled = this.shouldShowGroupingToggle() && catalogSearchOptions.get('showGroupings');

    if (currentPage === 1 && featuredCards.size) {
      // Display the first card row after the second item,
      // or after the first item if there's only 1 result.
      if (results.size === 1 || currentItemPosition === 2) {
        return (
          <PersonalizedPromotionsContainer
            debugEnabled={debugEnabled}
            position={currentItemPosition}
            cards={Immutable.List.of(featuredCards.first())}
          />
        );
      }

      // Display the second card row between the 7th and 8th items
      if (featuredCards.size > 1 && results.size > 7 && currentItemPosition === 7) {
        return (
          <PersonalizedPromotionsContainer
            debugEnabled={debugEnabled}
            position={currentItemPosition}
            cards={featuredCards.rest()}
          />
        );
      }
    }

    return null;
  }

  renderResultItems() {
    let key = 0;
    return this.props.results.map((result, index) => (
      <CSSTransition classNames="fade" timeout={{ enter: 500, exit: 500 }} key={key++}>
        <AboveTheFoldOnlyServerRender skip={index > 4} key={key++}>
          <SearchResultItem
            dataKey="search-result-item"
            order={index > this.props.featuredContentBlockPosition ? 2 : 0}
            showSeries={this.props.searchType === 'series'}
            key={result.representative?.get('id')}
            item={result.representative}
            itemPosition={index + 1}
            manifestations={result.manifestations}
            showJacketCover={this.props.currentView !== VIEW.SMALL}
            branches={this.props.branches}
            renderCards={() => this.renderPersonalizedPromotions(index)}
          />
        </AboveTheFoldOnlyServerRender>
      </CSSTransition>
    ));
  }

  renderGroupingToggle() {
    const { searchActions, catalogSearchOptions } = this.props;

    if (!this.shouldShowGroupingToggle()) {
      return null;
    }

    return (
      <div className="grouping-summary-debug hidden-xs">
        <Checkbox
          id="grouping-summary-debug"
          label="Search debug tools"
          handleChange={searchActions.toggleShowGroupings}
          checked={catalogSearchOptions.get('showGroupings')}
          inline
          labelBefore
        />
      </div>
    );
  }

  renderActiveFilters() {
    const { activeFilters, locked, singleBranch } = this.props;
    if (activeFilters.isEmpty()) {
      return null;
    }

    return (
      <div className="active-filters row">
        <div className="col-md-12">
          <AppliedFilters
            appliedFilters={activeFilters}
            hasPinnedToggle
            isPinned={locked}
            isSingleBranch={singleBranch}
            toggleIsPinned={this.toggleIsFiltersPinned}
            unapplyAllFilters={this.unapplyAllFilters}
            unapplyFilter={this.unapplyFilter}
          />
        </div>
      </div>
    );
  }

  renderDidNotFindExpandablePanel() {
    const { didNotFindOptions, query } = this.props;

    return (
      didNotFindOptions.get('isEnabled') && (
        <DidNotFindExpandablePanel query={query} didNotFindOptions={didNotFindOptions} />
      )
    );
  }

  render() {
    const { activeFilters, currentView, isDrawerOpen, isFetching, pagination, searchActions, sortBys } = this.props;

    return (
      <section className="cp-search-results">
        {this.renderActiveFilters()}

        <ScreenReaderMessage>
          <FormattedMessage id="search.results_heading_sr" tagName="h2" />
        </ScreenReaderMessage>

        <section className="result-controls btn-toolbar">
          <FacetDrawerToggle
            drawerActions={this.props.drawerActions}
            activeFilterCount={activeFilters
              .toList()
              .flatten(true)
              .count()}
          />
          <SortDropDown options={sortBys} />
          <ViewSelector currentView={currentView} searchActions={searchActions} />
          <PrintButton className="hidden-xs hidden-sm" url={this.getPrintUrl()} iconColor="#333" />
          <Pagination
            autoFocus={!isDrawerOpen}
            compact
            isFetching={isFetching}
            textKey="Search.paginationInfo"
            pagination={pagination}
          />
        </section>

        <section className="results-list row">
          <div className="col-md-12 results-list-items">
            <ul className="results">
              <TransitionGroup component={null}>
                {this.renderResultItems()}
                {this.renderFeatureInCatalog()}
              </TransitionGroup>
            </ul>
          </div>
        </section>
        <AboveTheFoldOnlyServerRender skip>
          <section className="bottom-controls">
            <Pagination showFirstPage isFetching={isFetching} textKey="Search.paginationInfo" pagination={pagination} />
            {this.renderGroupingToggle()}
          </section>
        </AboveTheFoldOnlyServerRender>
        <section className="did-not-find-panel">{this.renderDidNotFindExpandablePanel()}</section>
      </section>
    );
  }
}

const propTypes = {
  activeFilters: activeFiltersShape.isRequired,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  catalogSearchOptions: catalogSearchOptionsShape.isRequired,
  currentUser: userIdentityShape,
  currentView: PropTypes.oneOf(Object.values(VIEW)).isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func),
  featuredContentBlockPosition: PropTypes.number,
  isFetching: PropTypes.bool,
  locked: PropTypes.bool,
  searchActions: PropTypes.objectOf(PropTypes.func),
  searchType: searchTypeShape.isRequired,
  singleBranch: PropTypes.bool,
  sortBys: sortBysShape.isRequired,
  didNotFindOptions: didNotFindOptionsShape,
  pagination: paginationShape,
  results: ImmutablePropTypes.listOf(searchResultShape).isRequired,
  featuredCards: ImmutablePropTypes.listOf(cardShape).isRequired,
  cardsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  eventsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  isDrawerOpen: PropTypes.bool.isRequired,
  library: libraryShape.isRequired,
  query: PropTypes.string,
  router: routerShape.isRequired
};

SearchResults.contextType = Context;

SearchResults.propTypes = propTypes;

const SearchResultsContainer = props => {
  const isDrawerOpen = useSelector(state => state.getIn(['drawer', 'open'])) ?? false;

  return <SearchResults {...props} isDrawerOpen={isDrawerOpen} />;
};

export default withRouter(SearchResultsContainer);
